import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Globe2, Sparkles, Building2, Radio } from "lucide-react";
import LeadForm from "@/components/LeadForm";
import BookCallFlow from "@/components/BookCallFlow";
import { HERO } from "@/constants/testIds";

const TRUST = [
  { icon: Globe2, label: "Global learners" },
  { icon: Radio, label: "Live instructor-led" },
  { icon: Building2, label: "Corporate training" },
  { icon: Sparkles, label: "SAP · Workday · AI" },
];

function useDesktopLayout() {
  const query = "(min-width: 1024px)";
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const media = window.matchMedia(query);
    const update = (event) => setIsDesktop(event.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return isDesktop;
}

export default function Hero() {
  const isDesktop = useDesktopLayout();
  const [showMobileForm, setShowMobileForm] = useState(false);
  const [bookCall, setBookCall] = useState({ open: false, prefill: null });
  const onLeadSuccess = ({ prefill }) => setBookCall({ open: true, prefill });

  return (
    <section
      data-testid={HERO.root}
      className="relative pt-28 lg:pt-36 pb-16 lg:pb-24 overflow-hidden"
    >
      {/* Background ornaments */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-grid opacity-60" aria-hidden />
        <div
          className="absolute -top-32 -right-24 h-[520px] w-[520px] rounded-full opacity-30 blur-3xl"
          style={{ background: "radial-gradient(closest-side, var(--cyan), transparent)" }}
          aria-hidden
        />
      </div>

      <div className="mx-auto max-w-[1440px] px-5 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        {/* LEFT */}
        <div className="lg:col-span-7 zt-rise">
          <div className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-3 py-1.5 text-[12px] font-medium text-royal-deep shadow-[0_2px_0_rgba(15,42,115,0.04)]">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: "var(--cyan)" }} aria-hidden />
            ZaranTech · Global IT Training
          </div>

          <h1
            data-testid={HERO.headline}
            className="font-display text-[40px] leading-[1.04] sm:text-5xl lg:text-[64px] font-bold tracking-tight text-ink mt-5 text-balance"
          >
            Global IT Training for{" "}
            <span className="relative whitespace-nowrap">
              <span className="text-royal-deep">SAP</span>
            </span>
            ,{" "}
            <span className="text-royal-deep">Workday</span>,{" "}
            <span className="relative inline-block">
              <span className="relative z-10">AI</span>
              <span
                className="absolute -bottom-1 left-0 right-0 h-3 rounded-sm"
                style={{ background: "var(--cyan)", opacity: 0.45 }}
                aria-hidden
              />
            </span>{" "}
            &amp; Enterprise Teams
          </h1>

          <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-ink-soft">
            Live instructor-led, self-paced, and corporate training programs built for professionals
            and organizations across the globe.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/courses"
              data-testid={HERO.primaryCta}
              className="inline-flex items-center gap-2 rounded-md bg-royal-deep px-6 py-3.5 text-sm font-semibold text-white transition-all hover:bg-royal hover:shadow-[0_18px_36px_-14px_rgba(15,42,115,0.55)]"
            >
              Explore Courses <ArrowRight size={15} />
            </Link>
            <Link
              to="/contact"
              data-testid={HERO.secondaryCta}
              className="inline-flex items-center gap-2 rounded-md border border-ink/15 bg-white px-6 py-3.5 text-sm font-semibold text-ink hover:border-royal hover:text-royal transition-colors"
            >
              Talk to Training Advisor
            </Link>
          </div>

          <div data-testid={HERO.trustBadges} className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {TRUST.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2.5">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-cyan-soft text-royal-deep">
                  <Icon size={16} />
                </span>
                <span className="text-[12.5px] font-medium leading-tight text-ink">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT */}
        <div className="lg:col-span-5 relative zt-rise" style={{ animationDelay: "120ms" }}>
          <div className="relative">
            <div className="relative overflow-hidden rounded-[20px] aspect-[4/5] shadow-[0_30px_80px_-30px_rgba(15,42,115,0.45)]">
              {/* Self-hosted (was pulled from images.unsplash.com) -- removes a
                  third-party network hop from the page's largest-contentful-
                  paint element, and this file is already cropped to match
                  this frame's 4:5 shape, so there's no oversized source
                  wasting bytes on a crop the browser would just throw away. */}
              <img
                src="/homepage/hero-training-session.webp"
                alt="Enterprise IT training professionals collaborating in a modern workspace"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                width="480"
                height="600"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(6,18,41,0) 35%, rgba(6,18,41,0.6) 100%)",
                }}
                aria-hidden
              />
              <div className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full bg-white/95 backdrop-blur px-3 py-1.5 text-[11px] font-mono-ui uppercase tracking-[0.18em] text-royal-deep">
                Live · Cohort starting soon
              </div>
              {/* <div className="absolute right-5 bottom-5 max-w-[200px] rounded-xl bg-white/95 backdrop-blur p-3 text-[12px] leading-snug text-ink shadow-md">
                <p className="font-semibold text-royal-deep">SAP · Workday · AI</p>
                <p className="text-ink-soft mt-1">Live instructor-led training built for professionals.</p>
              </div> */}

              <div className="absolute left-1/2 top-20 -translate-x-1/2 z-10">
  <div className="rounded-2xl bg-white/95 backdrop-blur-md px-6 py-4 shadow-xl border border-white/70 text-center">
    <p className="text-lg font-bold text-royal-deep">
      SAP · Workday · AI
    </p>

    <p className="mt-1 text-sm text-ink-soft">
      Live instructor-led training built for professionals.
    </p>
  </div>
</div>
            </div>

            {/* Overlapping form card on desktop */}
            {isDesktop && <div className="absolute -left-12 bottom-8 w-[520px] xl:w-[560px] rounded-2xl border border-line bg-white p-5 shadow-[0_30px_80px_-30px_rgba(15,42,115,0.35)]">
              <p className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-royal">
                Get personal guidance
              </p>
              <h3 className="font-display text-lg font-bold text-ink mt-1.5 leading-snug">
                Find your training path
              </h3>
              <div className="mt-4">
                <LeadForm variant="compact" formName="home-page-enquiry-form-hero-section" onSuccess={onLeadSuccess} />
              </div>
            </div>}
          </div>

          {/* Mobile/tablet form below image */}
          {!isDesktop && <div className="mt-6 rounded-2xl border border-line bg-white p-5">
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-royal">
              Get personal guidance
            </p>
            <h3 className="font-display text-lg font-bold text-ink mt-1.5 leading-snug">
              Find your training path
            </h3>
            {showMobileForm ? (
              <div className="mt-4">
                <LeadForm variant="compact" formName="home-page-enquiry-form-hero-section" onSuccess={onLeadSuccess} />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowMobileForm(true)}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md bg-royal-deep px-5 py-3 text-sm font-semibold text-white hover:bg-royal"
                aria-expanded="false"
              >
                Get Course Guidance <ArrowRight size={15} />
              </button>
            )}
          </div>}
        </div>
      </div>

      <BookCallFlow
        open={bookCall.open}
        prefill={bookCall.prefill}
        onClose={() => setBookCall({ open: false, prefill: null })}
      />
    </section>
  );
}
